import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { Subscription, timer } from 'rxjs';
import { finalize, switchMap, takeWhile, tap } from 'rxjs/operators';
import { HttpHelpService, AdminBroadcastCreateModel, BroadcastDeliveryReportRow, BroadcastDeliveryReportSummary, BroadcastDeliveryStatus } from '../service/http-help.service';

export type BroadcastAudience = 'ALL' | 'MERCHANTS' | 'ACCOUNTANTS';

export type DispatchProgressSnapshot = {
  broadcastId: number;
  phase: string;
  recipients: { userId: number; label: string; at: number }[];
  done: boolean;
  error?: string;
};

@Component({
  selector: 'app-admin-push-notifications',
  templateUrl: './admin-push-notifications.component.html',
  styleUrls: ['./admin-push-notifications.component.scss'],
})
export class AdminPushNotificationsComponent implements OnInit, OnDestroy {
  curentLang = localStorage.getItem('curentLang') || 'ar';

  rows: any[] = [];
  total = 0;
  page = 1;
  limit = 20;
  totalPages = 1;

  /** Filter left-menu style UX */
  statusFilter: '' | 'PENDING' | 'SENT' | 'FAILED' = '';

  createForm = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    message: new FormControl('', [Validators.required, Validators.maxLength(4000)]),
    audience: new FormControl<BroadcastAudience>('ALL', Validators.required),
    saveDraft: new FormControl(false, []),
    scheduledAt: new FormControl<string>('', []),
    /** When sending now (not draft): if false, dispatch once and do not persist a CRUD row (requires API support). */
    persistHistory: new FormControl(true, []),
  });

  isCreateSubmitting = false;
  isSendSubmitting = false;

  deliveryReportBroadcastId: number | null = null;
  deliveryReportBroadcast: any | null = null;
  deliveryReportSummary: BroadcastDeliveryReportSummary | null = null;
  deliveryReportRows: BroadcastDeliveryReportRow[] = [];
  deliveryReportPage = 1;
  deliveryReportLimit = 50;
  deliveryReportTotal = 0;
  deliveryReportTotalPages = 1;
  deliveryReportHasDetailed = false;
  deliveryReportLoading = false;
  deliveryReportStatusFilter: '' | BroadcastDeliveryStatus = '';
  deliveryReportSearch = '';
  private deliveryReportSub: Subscription | null = null;
  private deliveryReportSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private saveDraftSyncSub: Subscription | null = null;

  /** While progress modal runs for a one-shot send (not saved to admin table). */
  private ephemeralProgressFlag = false;

  dispatchSnapshot: DispatchProgressSnapshot | null = null;
  activeProgressToken: string | null = null;
  private progressPollSub: Subscription | null = null;
  private progressModalRef: NgbModalRef | null = null;

  fcmDiagnostics: {
    firebase_admin_ready: boolean;
    users_with_device_token: number;
    hint: string;
  } | null = null;

  readonly audienceOptions: { value: BroadcastAudience; labelAr: string; labelEn: string; labelHe: string }[] =
    [
      { value: 'ALL', labelAr: 'الجميع', labelEn: 'All users', labelHe: 'כל המשתמשים' },
      { value: 'MERCHANTS', labelAr: 'التجار', labelEn: 'Merchants', labelHe: 'סוחרים' },
      { value: 'ACCOUNTANTS', labelAr: 'المحاسبون', labelEn: 'Accountants', labelHe: 'רואי חשבון' },
    ];

  readonly statusFilterOptions: {
    value: '' | 'PENDING' | 'SENT' | 'FAILED';
    labelAr: string;
    labelEn: string;
    labelHe: string;
  }[] = [
    { value: '', labelAr: 'كل الحالات', labelEn: 'All statuses', labelHe: 'כל הסטטוסים' },
    { value: 'PENDING', labelAr: 'مسودة / قيد الانتظار', labelEn: 'Draft / pending', labelHe: 'טיוטה / ממתין' },
    { value: 'SENT', labelAr: 'تم الإرسال', labelEn: 'Sent', labelHe: 'נשלח' },
    { value: 'FAILED', labelAr: 'فشل', labelEn: 'Failed', labelHe: 'נכשל' },
  ];

  readonly deliveryStatusFilterOptions: {
    value: '' | BroadcastDeliveryStatus;
    labelAr: string;
    labelEn: string;
    labelHe: string;
  }[] = [
    { value: '', labelAr: 'كل حالات التسليم', labelEn: 'All delivery statuses', labelHe: 'כל סטטוסי המסירה' },
    { value: 'DELIVERED', labelAr: 'تم التسليم', labelEn: 'Delivered', labelHe: 'נמסר' },
    { value: 'FAILED', labelAr: 'فشل الإرسال', labelEn: 'Failed', labelHe: 'נכשל' },
    { value: 'NO_TOKEN', labelAr: 'لا يوجد توكن', labelEn: 'No token', labelHe: 'אין אסימון' },
    { value: 'SENT', labelAr: 'تم الإرسال', labelEn: 'Sent', labelHe: 'נשלח' },
  ];

  constructor(
    private service: HttpHelpService,
    private spinner: NgxSpinnerService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.loadList();
    this.saveDraftSyncSub =
      this.createForm.get('saveDraft')?.valueChanges.subscribe((draft) => {
        if (draft) {
          this.createForm.get('persistHistory')?.setValue(true, { emitEvent: false });
        }
      }) ?? null;
  }

  ngOnDestroy(): void {
    this.stopProgressPolling(true);
    this.deliveryReportSub?.unsubscribe();
    this.deliveryReportSub = null;
    if (this.deliveryReportSearchTimer) {
      clearTimeout(this.deliveryReportSearchTimer);
      this.deliveryReportSearchTimer = null;
    }
    this.saveDraftSyncSub?.unsubscribe();
    this.saveDraftSyncSub = null;
  }

  /** Consistent SweetAlert look for this CRUD (global popup; styled via `styles.scss`). */
  private fireSwal(options: SweetAlertOptions) {
    const extra =
      options.customClass &&
      typeof options.customClass === 'object' &&
      !Array.isArray(options.customClass)
        ? options.customClass
        : {};
    const { customClass: _c, confirmButtonColor: _cbc, cancelButtonColor: _cbc2, ...rest } = options;
    return Swal.fire({
      buttonsStyling: false,
      ...rest,
      customClass: {
        popup: 'bn-push-swal-popup',
        title: 'bn-push-swal-title',
        htmlContainer: 'bn-push-swal-html',
        actions: 'bn-push-swal-actions',
        confirmButton: 'btn btn-primary bn-push-swal-btn bn-push-swal-confirm',
        cancelButton: 'btn btn-outline-secondary bn-push-swal-btn bn-push-swal-cancel',
        ...extra,
      },
    });
  }

  private bnModalOpts(extra?: Partial<NgbModalOptions>): NgbModalOptions {
    return {
      centered: true,
      scrollable: true,
      windowClass: 'bn-push-modal-window',
      backdropClass: 'bn-push-modal-backdrop',
      ...extra,
    };
  }

  t(ar: string, en: string, he: string): string {
    if (this.curentLang === 'ar') return ar;
    if (this.curentLang === 'he') return he;
    return en;
  }

  audienceLabel(code: string): string {
    const o = this.audienceOptions.find((x) => x.value === code);
    if (!o) return code || '—';
    return this.t(o.labelAr, o.labelEn, o.labelHe);
  }

  statusFilterLabel(row: (typeof this.statusFilterOptions)[0]): string {
    return this.t(row.labelAr, row.labelEn, row.labelHe);
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'SENT':
        return this.t('تم الإرسال', 'Sent', 'נשלח');
      case 'FAILED':
        return this.t('فشل الإرسال', 'Failed', 'נכשל');
      case 'PENDING':
        return this.t('مسودة / في الانتظار', 'Draft / pending', 'טיוטה / ממתין');
      default:
        return status || '—';
    }
  }

  /** Single modifier for `[ngClass]` on `.bn-status` */
  statusModifier(status: string): string {
    switch (status) {
      case 'SENT':
        return 'bn-status--sent';
      case 'FAILED':
        return 'bn-status--failed';
      case 'PENDING':
        return 'bn-status--pending';
      default:
        return 'bn-status--muted';
    }
  }

  scheduledAtLabel(row: any): string {
    const raw = row?.scheduledSendAt ?? row?.scheduled_send_at;
    if (!raw) return '—';
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  rowId(row: any): number {
    return row?.broadcast_notification_id ?? row?.id;
  }

  loadFcmDiagnostics(): void {
    this.service.getAdminBroadcastFcmDiagnostics().subscribe({
      next: (res) => {
        this.fcmDiagnostics = res ?? null;
      },
      error: () => {
        this.fcmDiagnostics = null;
      },
    });
  }

  fcmDiagnosticsOk(): boolean {
    return !!(
      this.fcmDiagnostics?.firebase_admin_ready &&
      (this.fcmDiagnostics?.users_with_device_token ?? 0) > 0
    );
  }

  loadList(): void {
    this.spinner.show();
    this.service
      .listAdminBroadcastNotifications({
        page: this.page,
        limit: this.limit,
        status: this.statusFilter || undefined,
      })
      .subscribe({
        next: (res) => {
          this.rows = res?.items ?? [];
          this.total = res?.total ?? 0;
          this.page = res?.page ?? this.page;
          this.limit = res?.limit ?? this.limit;
          this.totalPages = Math.max(
            1,
            res?.totalPages ?? (Math.ceil(this.total / this.limit) || 1),
          );
          this.spinner.hide();
          this.loadFcmDiagnostics();
        },
        error: (err) => {
          this.spinner.hide();
          this.showHttpError(err);
        },
      });
  }

  onStatusFilterChange(): void {
    this.page = 1;
    this.loadList();
  }

  setPage(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page = next;
    this.loadList();
  }

  openCreateModal(content: unknown): void {
    this.createForm.reset({
      title: '',
      message: '',
      audience: 'ALL',
      saveDraft: false,
      scheduledAt: '',
      persistHistory: true,
    });
    this.modalService.open(content, {
      ...this.bnModalOpts({ size: 'lg', backdrop: 'static' }),
    });
  }

  submitCreate(modal: { close: () => void }, progressTpl: TemplateRef<unknown>): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    const v = this.createForm.getRawValue();
    const saveDraft = !!v.saveDraft;
    const persistHistory = saveDraft ? true : !!v.persistHistory;
    let scheduledSendAt: string | undefined;
    if (saveDraft && v.scheduledAt) {
      const d = new Date(v.scheduledAt);
      if (!isNaN(d.getTime())) {
        scheduledSendAt = d.toISOString();
      }
    }
    this.isCreateSubmitting = true;
    this.spinner.show();
    const body: AdminBroadcastCreateModel = {
      title: String(v.title || '').trim(),
      message: String(v.message || '').trim(),
      audience: v.audience as string,
      dispatchImmediately: !saveDraft,
      scheduledSendAt: saveDraft ? scheduledSendAt ?? null : undefined,
      persistHistory,
    };
    this.service.createAdminBroadcastNotification(body).subscribe({
        next: (res) => {
          this.spinner.hide();
          this.isCreateSubmitting = false;
          modal.close();
          const token = res?.progressToken as string | undefined;
          if (token) {
            const ephemeral = !saveDraft && !persistHistory;
            this.openProgressMonitor(progressTpl, token, { ephemeral });
            return;
          }
          if (saveDraft) {
            this.fireSwal({
              icon: 'success',
              title: this.t('تم حفظ المسودة', 'Draft saved', 'טיוטה נשמרה'),
              text: this.t(
                'يمكنك الإرسال لاحقًا من زر «إرسال» في الجدول.',
                'Send later using the Send button in the table.',
                'ניתן לשלוח מאוחר יותר עם כפתור השליחה בטבלה.',
              ),
              timer: 6000,
            });
          } else if (!persistHistory) {
            this.fireSwal({
              icon: 'success',
              title: this.t('تم الإرسال', 'Sent', 'נשלח'),
              text: this.t(
                'إرسال لمرة واحدة — لن يظهر في جدول الإدارة.',
                'One-time send — it will not appear in the admin table.',
                'שליחה חד־פעמית — לא תופיע בטבלת הניהול.',
              ),
              timer: 6500,
            });
          } else {
            this.fireSwal({
              icon: 'success',
              title: this.t('تم الحفظ', 'Saved', 'נשמר'),
              text: '',
              timer: 2000,
            });
          }
          this.loadList();
        },
        error: (err) => {
          this.spinner.hide();
          this.isCreateSubmitting = false;
          this.showHttpError(err);
        },
      });
  }

  openProgressMonitor(
    content: TemplateRef<unknown>,
    token: string,
    opts?: { ephemeral?: boolean },
  ): void {
    this.ephemeralProgressFlag = !!opts?.ephemeral;
    this.stopProgressPolling(true);
    this.activeProgressToken = token;
    this.dispatchSnapshot = {
      broadcastId: -1,
      phase: this.t('بدء الإرسال…', 'Starting dispatch…', 'מתחיל שליחה…'),
      recipients: [],
      done: false,
    };
    this.progressModalRef = this.modalService.open(content, {
      ...this.bnModalOpts({ size: 'lg', backdrop: 'static', keyboard: false }),
    });
    this.progressPollSub = timer(0, 450)
      .pipe(
        switchMap(() => this.service.getAdminBroadcastDispatchProgress(token)),
        tap((snap) => {
          this.dispatchSnapshot = snap;
        }),
        takeWhile((snap) => !snap.done, true),
        finalize(() => {
          this.loadList();
        }),
      )
      .subscribe({
        next: (snap) => {
          if (!snap.done) return;
          const wasEphemeral = this.ephemeralProgressFlag;
          this.ephemeralProgressFlag = false;
          this.stopProgressPolling(true);
          if (snap.error) {
            this.fireSwal({
              icon: 'error',
              title: this.t('فشل الإرسال', 'Dispatch failed', 'השליחה נכשלה'),
              text: snap.error,
            });
            return;
          }
          this.fireSwal({
            icon: 'success',
            title: this.t('اكتمل الإرسال', 'Dispatch completed', 'השליחה הושלמה'),
            text: wasEphemeral
              ? this.t(
                  'لم يُحفظ في الجدول — إرسال لمرة واحدة.',
                  'Not saved to the table — one-time broadcast.',
                  'לא נשמר בטבלה — שידור חד־פעמי.',
                )
              : '',
            timer: wasEphemeral ? 6000 : 4500,
          });
        },
        error: () => {
          this.ephemeralProgressFlag = false;
          this.stopProgressPolling(true);
          this.fireSwal({
            icon: 'warning',
            title: this.t('توقف تتبع التقدم', 'Progress polling stopped', 'מעקב ההתקדמות נעצר'),
          });
        },
      });
  }

  closeProgressModal(): void {
    this.ephemeralProgressFlag = false;
    this.stopProgressPolling(true);
  }

  private stopProgressPolling(closeModal = false): void {
    this.progressPollSub?.unsubscribe();
    this.progressPollSub = null;
    this.activeProgressToken = null;
    if (closeModal && this.progressModalRef) {
      try {
        this.progressModalRef.close();
      } catch {
        /* noop */
      }
      this.progressModalRef = null;
    }
    this.dispatchSnapshot = null;
  }

  canSend(row: any): boolean {
    const s = row?.status;
    return s === 'PENDING' || s === 'FAILED';
  }

  canResend(row: any): boolean {
    return row?.status === 'SENT';
  }

  /** Pending / failed → dispatch now */
  sendFromTable(row: any, progressTpl: TemplateRef<unknown>): void {
    const id = this.rowId(row);
    if (!id) return;
    this.fireSwal({
      icon: 'question',
      title: this.t('تأكيد الإرسال', 'Confirm send', 'אשר שליחה'),
      text: this.t(
        'سيتم إرسال الإشعار إلى الجمهور المحفوظ.',
        'The saved audience will receive this notification.',
        'הקהל השמור יקבל את ההתראה.',
      ),
      showCancelButton: true,
      confirmButtonText: this.t('إرسال', 'Send', 'שלח'),
      cancelButtonText: this.t('إلغاء', 'Cancel', 'ביטול'),
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.isSendSubmitting = true;
      this.spinner.show();
      this.service.sendAdminBroadcastNotification(id, { resend: false }).subscribe({
        next: (res) => {
          this.spinner.hide();
          this.isSendSubmitting = false;
          const token = res?.progressToken as string | undefined;
          if (token) this.openProgressMonitor(progressTpl, token);
        },
        error: (err) => {
          this.spinner.hide();
          this.isSendSubmitting = false;
          this.showHttpError(err);
        },
      });
    });
  }

  resendFromTable(row: any, progressTpl: TemplateRef<unknown>): void {
    const id = this.rowId(row);
    if (!id) return;
    this.fireSwal({
      icon: 'warning',
      title: this.t('إعادة الإرسال؟', 'Resend?', 'לשלוח שוב?'),
      text: this.t(
        'سيتم إعادة بث نفس الرسالة إلى نفس الجمهور.',
        'The same message will be pushed again to the same audience.',
        'אותה הודעה תישלח שוב לאותו קהל.',
      ),
      showCancelButton: true,
      confirmButtonText: this.t('إعادة إرسال', 'Resend', 'שלח שוב'),
      cancelButtonText: this.t('إلغاء', 'Cancel', 'ביטול'),
      customClass: {
        confirmButton: 'btn btn-warning text-dark bn-push-swal-btn bn-push-swal-confirm',
      },
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.isSendSubmitting = true;
      this.spinner.show();
      this.service.sendAdminBroadcastNotification(id, { resend: true }).subscribe({
        next: (res) => {
          this.spinner.hide();
          this.isSendSubmitting = false;
          const token = res?.progressToken as string | undefined;
          if (token) this.openProgressMonitor(progressTpl, token);
        },
        error: (err) => {
          this.spinner.hide();
          this.isSendSubmitting = false;
          this.showHttpError(err);
        },
      });
    });
  }

  openDeliveryReportModal(content: unknown, row: any): void {
    const id = this.rowId(row);
    if (!id) return;
    this.deliveryReportBroadcastId = id;
    this.deliveryReportBroadcast = row;
    this.deliveryReportPage = 1;
    this.deliveryReportStatusFilter = '';
    this.deliveryReportSearch = '';
    this.deliveryReportRows = [];
    this.deliveryReportSummary = null;
    this.deliveryReportHasDetailed = false;
    this.modalService.open(content, this.bnModalOpts({ size: 'xl' }));
    this.loadDeliveryReport(true);
  }

  loadDeliveryReport(resetPage = false): void {
    const id = this.deliveryReportBroadcastId;
    if (!id) return;
    if (resetPage) {
      this.deliveryReportPage = 1;
    }
    this.deliveryReportLoading = true;
    this.deliveryReportSub?.unsubscribe();
    this.deliveryReportSub = this.service
      .getAdminBroadcastDeliveryReport(id, {
        page: this.deliveryReportPage,
        limit: this.deliveryReportLimit,
        status: this.deliveryReportStatusFilter || undefined,
        search: this.deliveryReportSearch || undefined,
      })
      .pipe(finalize(() => (this.deliveryReportLoading = false)))
      .subscribe({
        next: (res) => {
          this.deliveryReportBroadcast = res.broadcast ?? this.deliveryReportBroadcast;
          this.deliveryReportSummary = res.summary ?? null;
          this.deliveryReportRows = res.data ?? [];
          this.deliveryReportTotal = res.total ?? 0;
          this.deliveryReportPage = res.page ?? this.deliveryReportPage;
          this.deliveryReportLimit = res.limit ?? this.deliveryReportLimit;
          this.deliveryReportTotalPages = Math.max(1, res.totalPages ?? 1);
          this.deliveryReportHasDetailed = res.hasDetailedReport === true;
        },
        error: (err) => this.showHttpError(err),
      });
  }

  onDeliveryReportStatusChange(): void {
    this.loadDeliveryReport(true);
  }

  onDeliveryReportSearchInput(): void {
    if (this.deliveryReportSearchTimer) {
      clearTimeout(this.deliveryReportSearchTimer);
    }
    this.deliveryReportSearchTimer = setTimeout(() => {
      this.loadDeliveryReport(true);
    }, 350);
  }

  setDeliveryReportPage(next: number): void {
    if (next < 1 || next > this.deliveryReportTotalPages) return;
    this.deliveryReportPage = next;
    this.loadDeliveryReport();
  }

  deliveryStatusFilterLabel(row: (typeof this.deliveryStatusFilterOptions)[0]): string {
    return this.t(row.labelAr, row.labelEn, row.labelHe);
  }

  deliveryStatusLabel(status: BroadcastDeliveryStatus | string): string {
    switch (status) {
      case 'DELIVERED':
        return this.t('تم التسليم', 'Delivered', 'נמסר');
      case 'FAILED':
        return this.t('فشل الإرسال', 'Failed', 'נכשל');
      case 'NO_TOKEN':
        return this.t('لا يوجد توكن', 'No token', 'אין אסימון');
      case 'SENT':
        return this.t('تم الإرسال', 'Sent', 'נשלח');
      default:
        return status || '—';
    }
  }

  deliveryStatusModifier(status: BroadcastDeliveryStatus | string): string {
    switch (status) {
      case 'DELIVERED':
        return 'bn-delivery--delivered';
      case 'FAILED':
        return 'bn-delivery--failed';
      case 'NO_TOKEN':
        return 'bn-delivery--no-token';
      case 'SENT':
        return 'bn-delivery--sent';
      default:
        return 'bn-delivery--muted';
    }
  }

  merchantDisplayName(row: BroadcastDeliveryReportRow): string {
    if (this.curentLang === 'ar') {
      return row.merchant_name_ar || row.merchant_name_en || row.merchant_name_he || '—';
    }
    if (this.curentLang === 'he') {
      return row.merchant_name_he || row.merchant_name_en || row.merchant_name_ar || '—';
    }
    return row.merchant_name_en || row.merchant_name_ar || row.merchant_name_he || '—';
  }

  merchantMeta(row: BroadcastDeliveryReportRow): string {
    const parts: string[] = [];
    if (row.merchant_id != null) {
      parts.push(`#${row.merchant_id}`);
    }
    if (row.merchant_id_number) {
      parts.push(row.merchant_id_number);
    }
    return parts.length ? parts.join(' · ') : '—';
  }

  userDisplayName(row: BroadcastDeliveryReportRow): string {
    const name = row.user_name?.trim();
    if (name) return name;
    if (row.phone_number) return row.phone_number;
    return `#${row.user_id}`;
  }

  channelLabel(channel: string | null): string {
    if (channel === 'fcm') return 'FCM';
    if (channel === 'webpush') return this.t('ويب', 'Web push', 'Web push');
    return '—';
  }

  confirmDelete(row: any): void {
    const id = this.rowId(row);
    if (!id) return;
    this.fireSwal({
      icon: 'warning',
      title: this.t('حذف السجل؟', 'Delete record?', 'למחוק רשומה?'),
      text: this.t('لا يمكن التراجع.', 'This cannot be undone.', 'לא ניתן לבטל.'),
      showCancelButton: true,
      cancelButtonText: this.t('إلغاء', 'Cancel', 'ביטול'),
      confirmButtonText: this.t('حذف', 'Delete', 'מחק'),
      customClass: {
        confirmButton: 'btn btn-danger bn-push-swal-btn bn-push-swal-confirm',
      },
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.spinner.show();
      this.service.deleteAdminBroadcastNotification(id).subscribe({
        next: () => {
          this.spinner.hide();
          this.fireSwal({ icon: 'success', title: this.t('تم الحذف', 'Deleted', 'נמחק'), timer: 2500 });
          this.loadList();
        },
        error: (err) => {
          this.spinner.hide();
          this.showHttpError(err);
        },
      });
    });
  }

  invalidCreate(field: string): boolean {
    const c = this.createForm.get(field);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  private showHttpError(err: any): void {
    const msg =
      err?.error?.message ||
      err?.error?.messageEn ||
      err?.error?.messageAr ||
      err?.message ||
      this.t('فشل الطلب', 'Request failed', 'הבקשה נכשלה');
    this.fireSwal({ icon: 'error', title: msg });
  }
}
